import type {
  SceneAudioStimulus,
  SceneEnvironmentType,
  ScenePhotoStimulus,
  SceneStimulusCatalog,
  SceneStimulusEntry,
  SceneSurfaceType,
} from "../config/scene-stimulus-schema";

const ENVIRONMENTS = new Set<SceneEnvironmentType>(["indoor", "outdoor"]);
const SURFACES = new Set<SceneSurfaceType>(["wood", "tile", "gravel", "fallen_leaves"]);

export function parseSceneStimulusCatalog(value: unknown): SceneStimulusCatalog {
  if (!isRecord(value) || value.schemaVersion !== 1 || typeof value.catalogVersion !== "string") {
    throw new Error("Scene stimulus catalog header is invalid");
  }
  if (!Array.isArray(value.scenes) || value.scenes.length !== 4) {
    throw new Error("Scene stimulus catalog must contain exactly four scenes");
  }
  const scenes = value.scenes.map(parseScene);
  const sceneIds = new Set(scenes.map((scene) => scene.id));
  if (sceneIds.size !== scenes.length) throw new Error("Scene stimulus ids must be unique");
  const indoorCount = scenes.filter((scene) => scene.environmentType === "indoor").length;
  const outdoorCount = scenes.filter((scene) => scene.environmentType === "outdoor").length;
  if (indoorCount !== 2 || outdoorCount !== 2) throw new Error("Catalog requires two indoor and two outdoor scenes");
  const photoIds = scenes.flatMap((scene) => scene.photos.map((photo) => photo.id));
  if (new Set(photoIds).size !== photoIds.length) throw new Error("Photo ids must be unique across the catalog");
  return { schemaVersion: 1, catalogVersion: value.catalogVersion, scenes };
}

function parseScene(value: unknown, index: number): SceneStimulusEntry {
  if (!isRecord(value)) throw new Error(`Scene ${index} is invalid`);
  const id = requireString(value.id, `Scene ${index} id`);
  const environmentType = value.environmentType;
  const surfaceType = value.surfaceType;
  if (!ENVIRONMENTS.has(environmentType as SceneEnvironmentType)) throw new Error(`${id} environment type is invalid`);
  if (!SURFACES.has(surfaceType as SceneSurfaceType)) throw new Error(`${id} surface type is invalid`);
  if (!Array.isArray(value.photos) || value.photos.length !== 5) throw new Error(`${id} must contain five photos`);
  if (!Array.isArray(value.audio) || value.audio.length !== 3) throw new Error(`${id} must contain three audio clips`);
  if (value.enabled !== true || value.researchEligible !== false) throw new Error(`${id} prototype flags are invalid`);
  const photos = value.photos.map((photo, photoIndex) => parsePhoto(photo, id, photoIndex));
  const audio = value.audio.map((slot, audioIndex) => parseAudio(slot, id, audioIndex));
  if (new Set(photos.map((photo) => photo.viewIndex)).size !== 5) throw new Error(`${id} photo view indices must be unique`);
  if (new Set(audio.map((slot) => slot.takeIndex)).size !== 3) throw new Error(`${id} audio take indices must be unique`);
  return {
    id,
    slug: requireString(value.slug, `${id} slug`),
    displayName: requireString(value.displayName, `${id} displayName`),
    environmentType: environmentType as SceneEnvironmentType,
    surfaceType: surfaceType as SceneSurfaceType,
    prototypeUrl: requireString(value.prototypeUrl, `${id} prototypeUrl`),
    revealBuilderId: requireString(value.revealBuilderId, `${id} revealBuilderId`),
    photos,
    audio,
    enabled: true,
    researchEligible: false,
  };
}

function parsePhoto(value: unknown, sceneId: string, index: number): ScenePhotoStimulus {
  if (!isRecord(value)) throw new Error(`${sceneId} photo ${index} is invalid`);
  const viewIndex = value.viewIndex;
  if (!Number.isInteger(viewIndex) || (viewIndex as number) < 1 || (viewIndex as number) > 5) throw new Error(`${sceneId} photo view index is invalid`);
  if (value.width !== 1280 || value.height !== 720) throw new Error(`${sceneId} photos must be 1280x720`);
  const url = requireString(value.url, `${sceneId} photo url`);
  if (!url.endsWith(".png")) throw new Error(`${sceneId} photo must use PNG`);
  return {
    id: requireString(value.id, `${sceneId} photo id`),
    viewIndex: viewIndex as ScenePhotoStimulus["viewIndex"],
    label: requireString(value.label, `${sceneId} photo label`),
    url,
    width: 1280,
    height: 720,
  };
}

function parseAudio(value: unknown, sceneId: string, index: number): SceneAudioStimulus {
  if (!isRecord(value)) throw new Error(`${sceneId} audio ${index} is invalid`);
  const takeIndex = value.takeIndex;
  if (!Number.isInteger(takeIndex) || (takeIndex as number) < 1 || (takeIndex as number) > 3) throw new Error(`${sceneId} audio take index is invalid`);
  const url = requireString(value.url, `${sceneId} audio url`);
  if (!url.endsWith(".wav") || value.fallback !== "procedural_footstep") throw new Error(`${sceneId} audio source is invalid`);
  if (value.sampleRateHz !== 48000 || value.channels !== 2 || value.bitsPerSample !== 16 || value.durationSeconds !== 11.28) {
    throw new Error(`${sceneId} audio format metadata is invalid`);
  }
  return {
    id: requireString(value.id, `${sceneId} audio id`),
    takeIndex: takeIndex as SceneAudioStimulus["takeIndex"],
    url,
    sampleRateHz: 48000,
    channels: 2,
    bitsPerSample: 16,
    durationSeconds: 11.28,
    fallback: "procedural_footstep",
  };
}

function requireString(value: unknown, label: string): string {
  if (typeof value !== "string" || value.trim() === "") throw new Error(`${label} must be a non-empty string`);
  return value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
